import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import {
  AddCircleOutlined,
  CancelOutlined,
  Check,
  CloseOutlined,
  DeleteOutlineOutlined,
  DoubleArrowOutlined,
  FileCopyOutlined
} from '@mui/icons-material';

const initialCodes = [
  'A04.7 c. Diff colitis',
  'A08.11 norovirus',
  'A09 infectious gastroenteritis and colitis, unspecified',
  'A15.0 tuberculosis of lung',
  'A41.9 sepsis',
  'A64 unspecified sexually transmitted disease',
  'B349 viral infection, unspecified',
  'D259 leiomyoma',
  'D64.9 anemia, unspecified',
  'E03.9 hypothyroidism, unspecified',
  'E10.9 type 1 diabetes mellitus without complications',
  'E11.8 type 2 diabetes mellitus with unspecified complications',
  'E11.9 type 2 diabetes mellitus without complications',
  'E29.1 testicular hypofunction',
  'E55.9 vitamin d deficiency, unspecified',
  'E66.9 obesity, unspecified',
  'E78.2 mixed hyperlipidemia',
  'F09 organic brain syndrome',
  'F41.9 anxiety disorder, unspecified',
  'G06.0 brain abscess',
  'G25.3 nocturnal myoclonus',
  'G47.00 insomnia, unspecified',
  'G89.29 other chronic pain',
  'I10 hypertension',
  'I10 essential primary hypertension',
  'I50.9 congestive heart failure',
  'Icd005 test created by vaisakh',
  'J06.9 acute upper respiratory infection, unspecified',
  'J30.9 allergic rhinitis, unspecified'
];

const dialogPaperSx = {
  width: '95%',
  maxWidth: 1200,
  borderRadius: '4px',
  overflow: 'hidden'
};

const dialogHeaderSx = {
  height: 48,
  px: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: '1px solid #E0E0E0',
  bgcolor: '#fff'
};

const styles = {
  templateBody: {
    border: '1px solid #D4E5F9',
    bgcolor: '#fff',
    height: '100%'
  },
  table: {
    '& th': {
      padding: '8px 10px',
      fontSize: 13,
      fontWeight: 500,
      color: '#4A4A4A',
      borderBottom: '1px solid #D4E5F9',
      bgcolor: '#EFF5FC'
    },
    '& td': {
      padding: '8px 10px',
      color: '#4A4A4A',
      fontSize: 13,
      lineHeight: '16px',
      borderBottom: '1px solid #EEF3F8'
    }
  },
  textField: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      minHeight: 34,
      fontSize: 12,
      borderRadius: '2px',
      bgcolor: '#fff'
    },
    '& input': {
      padding: '7px 10px',
      fontSize: 12
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E6E9EC'
    }
  },
  addButton: {
    minWidth: 32,
    height: 30,
    px: 0.8,
    borderRadius: '3px',
    boxShadow: 'none'
  },
  listBox: {
    height: 'calc(100vh - 400px)',
    overflowY: 'auto'
  },
  list: {
    listStyle: 'none',
    m: 0,
    p: 0,
    '& li': {
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      py: 0.3,
      px: 0.5,
      cursor: 'pointer',
      borderRadius: '3px',
      color: '#4A4A4A',
      fontSize: 13,
      '&:hover': {
        bgcolor: '#EFF5FC'
      }
    },
    '& svg': {
      width: 15,
      height: 15,
      color: '#D8A85A'
    }
  },
  selectedList: {
    listStyle: 'none',
    m: 0,
    p: 0,
    '& li': {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 0.4,
      px: 0.5,
      borderRadius: '3px',
      color: '#4A4A4A',
      fontSize: 13,
      '&:hover': {
        bgcolor: '#EFF5FC'
      }
    },
    '& svg': {
      width: 17,
      height: 17,
      color: '#424242'
    }
  },
  footer: {
    textAlign: 'right',
    pt: 2
  }
};

const TemplateDialog = ({ open, type, onClose }) => {
  const title = type === 'cpt' ? 'CPT User Database' : 'ICD 10 User Database';

  const [availableCodes, setAvailableCodes] = useState(initialCodes);
  const [selectedCodes, setSelectedCodes] = useState([
    'A04.7 c. Diff colitis',
    'A08.11 norovirus',
    'A09 infectious gastroenteritis and colitis, unspecified',
    'E11.9 type 2 diabetes mellitus without complications',
    'E29.1 testicular hypofunction'
  ]);

  const [search, setSearch] = useState('');
  const [isEdit, setIsEdit] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newDescription, setNewDescription] = useState('');

  const filteredCodes = useMemo(() => {
    const value = search.trim().toLowerCase();

    return availableCodes.filter(
      item => !selectedCodes.includes(item) && item.toLowerCase().includes(value)
    );
  }, [availableCodes, selectedCodes, search]);

  const handleAddToSelected = item => {
    setSelectedCodes(prev => [...prev, item]);
  };

  const handleAddAll = () => {
    setSelectedCodes(prev => [...prev, ...filteredCodes]);
  };

  const handleRemoveSelected = item => {
    setSelectedCodes(prev => prev.filter(code => code !== item));
  };

  const handleSaveNewCode = () => {
    if (!newCode || !newDescription) return;

    const value = `${newCode} ${newDescription}`.trim();

    setAvailableCodes(prev => [value, ...prev]);
    setNewCode('');
    setNewDescription('');
    setIsEdit(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: dialogPaperSx }}
    >
      <Box sx={dialogHeaderSx}>
        <Typography sx={{ fontSize: 18, fontWeight: 600 }}>{title}</Typography>

        <IconButton size="small" onClick={onClose}>
          <CloseOutlined />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={styles.templateBody}>
              <Table sx={styles.table}>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TextField
                        placeholder="Search user codes and descriptions"
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                        sx={styles.textField}
                      />
                    </TableCell>

                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      {!isEdit ? (
                        <>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleAddAll}
                            sx={{ ...styles.addButton, mr: 0.5 }}
                          >
                            <DoubleArrowOutlined />
                          </Button>

                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => setIsEdit(true)}
                            sx={styles.addButton}
                          >
                            <AddCircleOutlined />
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => setIsEdit(false)}
                          sx={{
                            ...styles.addButton,
                            bgcolor: '#B9B9B9'
                          }}
                        >
                          <CancelOutlined />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>

                {isEdit && (
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Slide direction="down" in={isEdit} timeout={200}>
                          <Box sx={{ display: 'flex' }}>
                            <TextField
                              placeholder="Input Code"
                              value={newCode}
                              onChange={event => setNewCode(event.target.value)}
                              sx={{ ...styles.textField, width: 90 }}
                            />

                            <TextField
                              placeholder="Input Description"
                              value={newDescription}
                              onChange={event =>
                                setNewDescription(event.target.value)
                              }
                              sx={{
                                ...styles.textField,
                                width: 'calc(100% - 95px)',
                                ml: 0.5
                              }}
                            />
                          </Box>
                        </Slide>
                      </TableCell>

                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={handleSaveNewCode}
                          sx={{ color: '#03A484' }}
                        >
                          <Check />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                )}

                <TableBody>
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Box sx={styles.listBox}>
                        <Box component="ul" sx={styles.list}>
                          {filteredCodes.map(item => (
                            <Box
                              component="li"
                              key={item}
                              onClick={() => handleAddToSelected(item)}
                            >
                              <IconButton size="small">
                                <FileCopyOutlined />
                              </IconButton>
                              {item}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={styles.templateBody}>
              <Table sx={styles.table}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ height: 49 }}>
                      <Typography component="b" sx={{ fontSize: 13 }}>
                        Selected CPT
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Box sx={styles.listBox}>
                        <Box component="ul" sx={styles.selectedList}>
                          {selectedCodes.map(item => (
                            <Box component="li" key={item}>
                              <Box>{item}</Box>

                              <IconButton
                                size="small"
                                onClick={() => handleRemoveSelected(item)}
                              >
                                <DeleteOutlineOutlined />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Grid>

        </Grid>
      </DialogContent>
       <DialogActions
            sx={{
              p: 1.5,
              borderTop: '1px solid #E0E0E0',
              gap: 0
            }}>
            <Button
              onClick={onClose}
              size='small'
              sx={{
                height: 38,
                px: 2,
                fontSize: 13,
                fontWeight: 500,
                color: '#616161',
                textTransform: 'uppercase'
              }}>
              Cancel
            </Button>
      
            <Button
              variant="contained"
              onClick={onClose}
              size='small'
              sx={{
                px: 2,
                height: 38,
                fontSize: 13,
                fontWeight: 500,
                textTransform: 'uppercase',
                borderRadius: '4px',
                bgcolor: '#5697D4',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#1976D2',
                  boxShadow: 'none'
                },
                '&.Mui-disabled': {
                  bgcolor: '#E9E9E9',
                  color: '#BDBDBD'
                }
              }}>
             Save
            </Button>
          </DialogActions>
      
    </Dialog>
  );
};

export default TemplateDialog;